import React, { useEffect, useState, useRef } from "react";
import {
  FaFacebook,
  FaInstagram,
  FaBullhorn,
  FaChartLine,
  FaCheckCircle,
  FaRocket,
  FaUsers,
  FaMobileAlt,
  FaArrowRight,
  FaPlay,
} from "react-icons/fa";
import { Link } from "react-router-dom";

/* ── Animated Counter ── */
const Counter = ({ end, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    const target = parseInt(end, 10);
    const dur = 1800;
    let startTs = null;
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min((ts - startTs) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(target * ease));
      if (p < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [started, end]);
  return <span ref={ref}>{count}{suffix}</span>;
};

export default function MetaAds() {

  // const stats = [
  //   { end: "500", suffix: "+", label: "Campaigns Delivered", icon: <FaBullhorn /> },
  //   { end: "98",  suffix: "%", label: "Client Satisfaction",  icon: <FaUsers /> },
  //   { end: "3",   suffix: "x", label: "Average ROAS",         icon: <FaChartLine /> },
  //   { end: "24",  suffix: "/7",label: "Ad Monitoring",        icon: <FaMobileAlt /> },
  // ];

  const services = [
    {
      icon: <FaFacebook size={28} />,
      title: "Facebook Ads",
      desc: "Precision-targeted campaigns that put your brand in front of millions of ready-to-buy customers.",
      gradient: "from-blue-500 to-cyan-500",
      tag: "Core Service",
    },
    {
      icon: <FaInstagram size={28} />,
      title: "Instagram Ads",
      desc: "Visually captivating Stories, Reels and Feed ads that stop the scroll and drive action.",
      gradient: "from-pink-500 to-violet-500",
      tag: "High Engagement",
    },
    {
      icon: <FaBullhorn size={28} />,
      title: "Brand Awareness",
      desc: "Build recognition across Meta's entire ecosystem with creative campaigns that stick.",
      gradient: "from-orange-400 to-rose-500",
      tag: "Top of Funnel",
    },
    {
      icon: <FaChartLine size={28} />,
      title: "Lead Generation",
      desc: "Conversion-optimized funnels that turn cold traffic into qualified leads consistently.",
      gradient: "from-emerald-400 to-cyan-500",
      tag: "Bottom of Funnel",
    },
    {
      icon: <FaRocket size={28} />,
      title: "Retargeting",
      desc: "Re-engage warm audiences who visited your site or interacted with your content.",
      gradient: "from-violet-500 to-purple-600",
      tag: "High ROI",
    },
    {
      icon: <FaUsers size={28} />,
      title: "Lookalike Audiences",
      desc: "Find new customers who mirror your best buyers using Meta's powerful audience tools.",
      gradient: "from-amber-400 to-orange-500",
      tag: "Scaling",
    },
  ];

  const included = [
    { label: "Full Campaign Setup",        sub: "Strategy, ad sets, creatives — built from the ground up." },
    { label: "Audience Research",          sub: "Targeting by interest, behavior, lookalike and custom audiences." },
    { label: "Pixel & CAPI Integration",   sub: "Server-side tracking for accurate attribution and retargeting." },
    { label: "Creative Direction",         sub: "Ad copy, visuals and video briefs optimized for conversions." },
    { label: "A/B Split Testing",          sub: "Systematic testing of headlines, audiences and creatives." },
    { label: "Monthly Performance Report", sub: "Clear reports with ROAS, CPL, CTR breakdowns every month." },
  ];

  const process = [
    { num: "01", title: "Strategy",  desc: "Understand your goals, audience, and competition deeply." },
    { num: "02", title: "Creative",  desc: "Design scroll-stopping ads tailored to your brand identity." },
    { num: "03", title: "Launch",    desc: "Go live with structured campaigns and pixel tracking ready." },
    { num: "04", title: "Optimise",  desc: "Continuously improve performance and cut wasted ad spend." },
  ];

  return (
    <div className="bg-[#030712] min-h-screen text-white pt-24 lg:pt-28 overflow-x-hidden selection:bg-cyan-500 selection:text-white" style={{ fontFamily: 'emoji' }}>


      {/* ── HERO ── */}
      <section className="relative min-h-[90vh] flex items-center border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[140px] animate-orb-drift" />
          <div className="absolute bottom-[-5%] left-[-5%] w-[420px] h-[420px] bg-violet-700/10 rounded-full blur-[120px] animate-orb-drift" style={{ animationDelay: "4s" }} />
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="1" fill="#0EA5E9" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030712]/30 to-[#030712]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-16 lg:py-0">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">

            {/* Left copy */}
            <div className="text-center lg:text-left flex flex-col items-center lg:items-start">
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-black tracking-[0.15em] uppercase mb-8 backdrop-blur-md animate-badge-pop">
                <span className="w-2 h-2 rounded-full bg-[#0EA5E9] animate-pulse shrink-0" />
                Meta Ads Management
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-8">
                <span className="text-white">Let's Run Ads</span><br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#38BDF8] to-[#818CF8]">
                  That Actually
                </span><br />
                <span className="text-white">Work.</span>
              </h1>

              <h3 className="text-xl sm:text-2xl font-semibold text-cyan-400 mb-4 tracking-wide">
                Drive High-Quality Leads and Massive ROI with Precision Targeting
              </h3>

              <p className="text-gray-400 text-base md:text-lg leading-relaxed font-light mb-10 max-w-xl">
                We build and manage high-ROI Facebook & Instagram ad campaigns — from creative strategy to pixel tracking — so you get more leads, more sales and more growth.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto relative z-20">
                <Link to="/contact" className="group flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white rounded-2xl font-black tracking-wide hover:scale-105 transition-all duration-300 shadow-[0_0_40px_rgba(14,165,233,0.35)] w-full sm:w-auto">
                  Get Free Audit
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <a href="https://wa.me/919699938509?text=Hello%20Vishwa%20Digitech!%20I%20want%20to%20talk%20to%20an%20expert%20about%20Meta%20Ads." target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold text-gray-300 border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:text-white backdrop-blur-md transition-all duration-300 hover:border-cyan-500/30 w-full sm:w-auto">
                  Talk to an Expert
                </a>
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mt-12">
                {["Facebook Partner", "Instagram Ads", "Meta Business Suite"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-500 text-xs font-semibold">
                    <FaCheckCircle className="text-cyan-500" />{t}
                  </div>
                ))}
              </div>
            </div>

            {/* Right image */}
            <div className="relative hidden lg:flex items-center justify-center animate-hero-float">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/15 via-violet-500/10 to-transparent rounded-[3rem] blur-2xl" />
              <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_30px_100px_rgba(14,165,233,0.15)]">
                <img
                  src="https://images.unsplash.com/photo-1611926653458-09294b3142bf?w=900&auto=format&fit=crop"
                  alt="Meta Ads Dashboard"
                  className="w-full max-w-[560px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/60 via-transparent to-transparent" />
              </div>
              <div className="absolute -top-5 -right-5 bg-[#0A0F1F]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-4 shadow-2xl animate-badge-pop">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">ROAS This Month</p>
                <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">3.8x</p>
                <p className="text-[10px] text-emerald-400 font-bold mt-1">↑ 22% vs last month</p>
              </div>
              <div className="absolute -bottom-5 -left-5 bg-[#0A0F1F]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-4 shadow-2xl animate-badge-pop" style={{ animationDelay: "0.2s" }}>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Cost Per Lead</p>
                <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">₹48</p>
                <p className="text-[10px] text-emerald-400 font-bold mt-1">↓ 35% optimized</p>
              </div>
            </div>

          </div>
        </div>
      </section>

   

      {/* ── SERVICES ── */}
      <section className="py-24 relative z-10 border-b border-white/5 overflow-hidden">
        <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-violet-600/6 rounded-full blur-[150px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-400 text-xs font-black tracking-widest uppercase mb-6">
              Our Services
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white mb-5 leading-[1.1]">
              Everything You Need<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#818CF8]">
                to Win on Meta
              </span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto font-light">
              Full-service Meta advertising — strategy, creative, targeting, tracking and reporting — all under one roof.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-[2rem] border border-white/[0.07] hover:border-white/20 bg-[#0A0F1F]/50 backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_0_50px_rgba(14,165,233,0.1)] p-8"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${svc.gradient} opacity-0 group-hover:opacity-[0.06] transition-opacity duration-700`} />
                <div className="relative z-10">
                  <span className={`inline-block text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded-full bg-gradient-to-r ${svc.gradient} text-white mb-6 shadow-md`}>
                    {svc.tag}
                  </span>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${svc.gradient} p-[1.5px] mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-xl`}>
                    <div className="w-full h-full bg-[#0A0F1F] rounded-2xl flex items-center justify-center text-white">
                      {svc.icon}
                    </div>
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white mb-3 tracking-tight">{svc.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base font-light">{svc.desc}</p>
                  <div className="flex items-center gap-2 mt-6 text-gray-500 group-hover:text-cyan-400 text-sm font-bold transition-colors duration-300">
                    Learn more <FaArrowRight className="group-hover:translate-x-1 transition-transform text-xs" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="py-24 relative z-10 border-b border-white/5 bg-white/[0.01]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 text-xs font-black tracking-widest uppercase mb-6">
            How We Work
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 tracking-tight">
            Our 4-Step <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#7DD3FC]">Process</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg font-light mb-16 max-w-xl mx-auto">
            From zero to scaled — a repeatable system that delivers results every time.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {process.map((p, i) => (
              <div key={i} className="relative group bg-[#0A0F1F]/50 rounded-[2rem] border border-white/[0.07] hover:border-cyan-500/30 p-8 transition-all duration-300 hover:-translate-y-1 text-left">
                <div className="absolute -inset-[1px] rounded-[2rem] bg-gradient-to-b from-cyan-500/0 via-cyan-500/10 to-violet-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-[1px]" />
                <div className="relative z-10">
                  <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-[#0EA5E9]/40 to-[#7DD3FC]/20 mb-5 leading-none">{p.num}</p>
                  <h3 className="text-lg font-black text-white mb-2">{p.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ── */}
      <section className="py-24 relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-14 lg:gap-20 items-center">

            <div className="relative w-full lg:w-[48%]">
              <div className="absolute -inset-4 bg-gradient-to-br from-cyan-500/12 to-violet-500/12 rounded-[3rem] blur-2xl" />
              <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(14,165,233,0.1)]">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&auto=format&fit=crop"
                  alt="Campaign analytics"
                  className="w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/60 via-transparent to-transparent" />
              </div>
            </div>

            <div className="w-full lg:w-[52%]">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs font-black tracking-widest uppercase mb-6">
                Full Package
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight leading-[1.1]">
                What's{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#7DD3FC]">Included?</span>
              </h2>
              <p className="text-gray-400 mb-8 text-base font-light leading-relaxed">
                Every plan comes with a complete suite of ad management deliverables — no hidden extras.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {included.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 bg-[#0A0F1F]/60 border border-white/[0.06] rounded-2xl px-5 py-4 hover:border-cyan-500/30 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] flex items-center justify-center shrink-0 mt-0.5 shadow-lg">
                      <FaCheckCircle className="text-white text-xs" />
                    </div>
                    <div>
                      <p className="font-black text-white text-sm leading-snug">{item.label}</p>
                      <p className="text-gray-500 text-xs mt-1 leading-relaxed">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      
     </div>
  );
}